import React, { useRef } from 'react';
import { Drawer, DrawerContent, DrawerTitle, DrawerDescription, DrawerClose } from "@/components/ui/drawer";
import { Bundle } from "@/types/bundle";
import { Book } from "@/types/book";
import { CountdownTimer } from "@/components/CountdownTimer";
import { X, Calendar } from "lucide-react";
import { motion } from "framer-motion";
import { EnhancedBookCover } from "@/components/ui/enhanced-book-cover";

interface CinemaComingSoonModalProps {
  isOpen: boolean;
  onClose: () => void;
  bundle: Bundle;
  books: Book[];
  originPosition?: { x: number; y: number };
}

export function CinemaComingSoonModal({
  isOpen,
  onClose,
  bundle,
  books,
  originPosition
}: CinemaComingSoonModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  const unlockDate = bundle.unlock_date ? new Date(bundle.unlock_date) : bundle.start_date ? new Date(bundle.start_date) : null;

  // Vaul Drawer provides native-like swipe to close. No custom gestures needed.

  return (
    <Drawer open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }} dismissible={true} snapPoints={[1]} activeSnapPoint={1}>
      <DrawerContent ref={modalRef} className="w-full h-full mx-auto bg-background/95 backdrop-blur-xl border-border/40 p-0 overflow-hidden shadow-2xl">
        <div className="relative">
          {/* Grabber */}
          <div className="pt-3 pb-2 flex justify-center">
            <div className="h-1.5 w-12 rounded-full bg-muted-foreground/30" />
          </div>

          {/* A11y */}
          <DrawerTitle className="sr-only">Coming Soon: {bundle.title}</DrawerTitle>
          <DrawerDescription className="sr-only">Preview of upcoming books and countdown.</DrawerDescription>

          {/* Close Button - lower */}
          <DrawerClose asChild>
            <button
              onClick={onClose}
              className="absolute top-16 right-6 z-50 w-8 h-8 rounded-full bg-muted/60 backdrop-blur-md border border-border/40 flex items-center justify-center text-foreground/60 hover:bg-muted/80 hover:text-foreground transition-all duration-200"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>
          </DrawerClose>

          {/* Animated container */}
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ type: "spring", stiffness: 300, damping: 28 }}
            className="relative"
          >
            {/* Header */}
            <div className="pt-14 pb-8 px-8 text-center">
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.08 }}>
                <div className="w-16 h-16 mx-auto mb-6 bg-gradient-to-br from-primary/20 to-primary/40 rounded-2xl flex items-center justify-center backdrop-blur-sm border border-primary/20">
                  <Calendar className="w-8 h-8 text-primary" />
                </div>

                <h2 className="text-2xl font-semibold text-foreground mb-3 tracking-tight">
                  Coming Soon
                </h2>

                <h3 className="text-lg font-medium text-primary mb-2">
                  {bundle.title}
                </h3>

                {bundle.description && (
                  <p className="text-muted-foreground text-sm leading-relaxed max-w-xs mx-auto">
                    {bundle.description}
                  </p>
                )}
              </motion.div>
            </div>

            {/* Book Preview Grid */}
            <div className="px-8 pb-8">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.12, duration: 0.4 }}
                className="grid grid-cols-3 gap-3 mb-8"
              >
                {books.slice(0, 3).map((book, index) => (
                  <motion.div
                    key={book.id}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.18 + index * 0.06, duration: 0.3 }}
                    className="relative group"
                  >
                    <div className="aspect-[3/4] rounded-2xl overflow-hidden bg-muted/40 border border-border/40">
                      <img
                        src={book.cover_image_url || ''}
                        alt={`${book.title} cover - coming soon`}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                      {/* Subtle lock overlay */}
                      <div className="absolute inset-0 bg-background/60 backdrop-blur-[2px] flex items-center justify-center">
                        <div className="w-6 h-6 rounded-full bg-muted/80 backdrop-blur-sm border border-border/40 flex items-center justify-center">
                          <div className="w-3 h-3 border-2 border-muted-foreground/40 rounded-sm border-t-0 relative">
                            <div className="absolute -top-1 left-0.5 w-2 h-1.5 border-2 border-muted-foreground/40 border-b-0 rounded-t-sm" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            </div>

            {/* Countdown Section */}
            {unlockDate && (
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.22, duration: 0.4 }}
                className="px-8 pb-8 flex justify-center"
              >
                <CountdownTimer
                  targetDate={unlockDate}
                  size="md"
                />
              </motion.div>
            )}

            {/* Bottom spacing */}
            <div className="pb-8" />
          </motion.div>
        </div>
      </DrawerContent>
    </Drawer>
  );
}